<template>
    <confirmation-dialog
        ref="modal"
        :label="t(`dialog.label`)"
        :description="canDelete ? t(`dialog.description`) : t(`dialog.cantDeleteDescription`)"
    >
        <template #default>
            <text-heading>{{ t(`dialog.label`) }}</text-heading>
            <p>
                <template v-if="checkingCanDelete">
                    <spinner-loader />
                </template>
                <template v-else-if="canDelete">
                    {{ t(`dialog.description`) }}
                </template>
                <template v-else>
                    {{ t(`dialog.cantDeleteDescription`) }}
                </template>
            </p>
        </template>
        <template #actions="{ bindings }">
            <flex-container align-items="center" justify-content="flex-end">
                <button-text @click="bindings.overlay.closeOverlay()">
                    {{ t('button.cancel') }}
                </button-text>
                <button-block
                    :color="'red-500'"
                    :disabled="!canDelete"
                    @click="removeTeam"
                >
                    {{ t('button.delete') }}
                </button-block>
            </flex-container>
        </template>
    </confirmation-dialog>
</template>

<script setup lang="ts">
    import { type ExposedOverlay, NotificationBlock, SpinnerLoader, useInteractionBlocker, useNotify } from '@keyvue/one-ui';
    import { computed, onMounted, ref } from 'vue';
    import useOverlayAsRoute from '@/services/keyvue-ui/composables/useOverlayAsRoute';
    import { useRouter } from 'vue-router';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { BrandId, DeleteBrandCommand, CanDeleteBrandCommand } from '@keyvue/one-api-client';
    import BrandDeletedEvent, { type BrandDeletedEventParameters } from '@/company/brand/events/BrandDeletedEvent';
    import { useI18n } from 'vue-i18n';
    import messages from './BrandDeleteView.messages.json';

    const { t } = useI18n({ messages });
    const modal = ref<ExposedOverlay>();
    const canDelete = ref(false);
    const checkingCanDelete = ref(true);
    const router = useRouter();
    const eventBus = useEventBus();
    const { notify } = useNotify();
    const { client } = useClient();
    const { blockInteractionForLoad } = useInteractionBlocker();

    const brandId = computed<string>(() => router.currentRoute.value.params.brandId.toString());

    onMounted(async () => {
        canDelete.value = await client.dispatch(new CanDeleteBrandCommand(new BrandId(brandId.value)));
        checkingCanDelete.value = false;
    });

    useOverlayAsRoute({
        overlay: modal,
        isSideSheet: false,
        redirectMethod: async () => {
            await new Promise((resolve) => {
                setTimeout(resolve, 1000);
            });

            await router.push({ name: 'brand-list' });
        },
    });

    async function removeTeam() {
        const clearBlock = blockInteractionForLoad(t('notification.interactionBlocker'));

        try {
            await client.dispatch(new DeleteBrandCommand(new BrandId(brandId.value)));
            eventBus.dispatch<BrandDeletedEventParameters>(BrandDeletedEvent, { brandId: brandId.value });

            notify({
                component: NotificationBlock,
                duration: 4000,
                props: {
                    title: t('notification.success.title'),
                    text: t('notification.success.text'),
                    color: 'green-500',
                },
            });
        } catch (e) {
            notify({
                component: NotificationBlock,
                duration: 4000,
                props: {
                    title: t('notification.error.title'),
                    text: t('notification.error.text'),
                    color: 'red-500',
                },
            });
        } finally {
            clearBlock();
            await router.push({ name: 'brand-list' });
        }
    }
</script>
